/*
 * @Author: zenghao
 * @Date: 2022-04-12 15:27:04
 * @LastEditors: mingyang_liu 18268117011@163.com
 * @LastEditTime: 2022-08-09 14:19:30
 * @Description: 请填写简介
 */
import { useEffect, useState } from 'react'

import { exportExcel, buildExcelColumns } from '@/pages/dashboard/utils/utils'
import { SwitchChartProps, Option, ChartOption, TableOption, ChartType } from '../types'
import { CHART_TYPE, EXCEL_COLUMN } from '../constants'

const useOptions = (props: SwitchChartProps) => {
  const { BAR, LINE, PIE, TABLE } = CHART_TYPE
  const {
    title = '',
    options = {},
    tabList,
    chartList,
    config,
    activeChart: activeChartProps,
    defaultActiveChart,
    onDownLoad,
    onTabChange,
    onChartChange
  } = props

  const [activeTab, setActiveTab] = useState<any>(tabList?.[0]?.value ?? '')
  const [activeChart, setActiveChart] = useState(defaultActiveChart ?? chartList?.[0] ?? '')
  const [chartOptions, setChartOptions] = useState({
    [BAR]: {} as any,
    [LINE]: {} as any,
    [TABLE]: {} as any,
    [PIE]: {} as any
  } as {
    [key: string]: any
  })

  // 根据图表类型格式化数据
  const formatDataByChartType = (chartType: ChartType, options: any, config: any = {}, isCompare=false) => {
    const formatMap = {
      [BAR]: () => {
        const { subTitle } = config
        // 数据配置
        const seriesBase = {
          type: 'bar',
          barMaxWidth: 80,
          cursor: 'pointer',
        }
        // 图例
        const legend: string[] = []
        const yAxis = {}
        const series: {
          [ket: string]: any
        } = []
        let xAxisData: string[] = []


        options?.forEach?.((option,index) => {
          const { name = '', data = [], type = 'bar', stack } = option
          legend.push(name ?? '')

          // 只初始化一次横坐标
          if (xAxisData?.length <= 0) {
            xAxisData = data?.map(item => item.x || '')
          }

          let temp = {
            ...seriesBase,
            name,
            type,

            // showSymbol: false,
            data: data?.map(item => item.y || '')
          } as any

          if (chartType === 'bar') {
            temp.stack = stack
          }

          if(chartType === 'line' && isCompare) {
            const flag = index % 2 === 0;
            // 保证当前日期在上层
            temp.z = flag ? options?.length + index : index
            temp.lineStyle =  {
              type: flag ? 'solid' : 'dotted'
            }
          }
          series.push(temp)
        })

        return {
          legend,
          series,
          xAxisData,
          yAxis,
          subTitle
        }
      },
      [LINE]: () => {
        const { subTitle } = config
        const lineOption = formatMap[BAR]()
        return {
          ...lineOption,
          series: lineOption?.series?.map(item => ({
            ...item,
            type: 'line'
          })),
          subTitle
        }
      },
      [TABLE]: () => {
        const { subTitle } = config

        return {
          ...(options || {}),
          subTitle
        }
      },
      [PIE]: () => {
        const {
          isLastDay = false,
          isSort = false,
          subTitle,
          chartData: chartDataProps,
          legendData: legendDataProps
        } = config as any
        let legendData: string[] = []
        const chartData: Array<{
          name: string
          value: string
        }> = []
        // let subTitle: string = ''
        options?.forEach?.(option => {
          const { name = '', data = [] } = option

          // 若当前数据不能叠加则取最后的数据
          const value = (() => {
            if (isLastDay) {
              // if (!subTitle) subTitle = data[data?.length - 1]?.x

              return data[data?.length - 1]?.y || ''
            } else {
              // if (!subTitle) subTitle = data[0]?.x + '~' + data[data.length - 1]?.x
              return data?.map(item => ((item.y as any) / 1))?.reduce((current, next) => current + next, 0)
            }
          })()

          legendData.push(name)
          chartData.push({
            name,
            value: String(value)
          })
        })

        const newChartData = chartDataProps ?? chartData
        // 增加数据排序功能
        if(isSort && newChartData.some(v => v.value)) {
          newChartData.sort((a, b) => Number(String(b.value).replace(/,|-|%|/g, '')) - Number(String(a.value).replace(/,|-|%|/g, '')))
          legendData = newChartData.map(v => v.name).filter(v => v !== 'Others')
          newChartData.findIndex(v => v.name === 'Others') > -1 && legendData.push('Others')
        }

        return {
          legendData: legendDataProps ?? legendData,
          chartData: newChartData.some(v => v.value) ? newChartData : [],
          subTitle
        }
      },
    }

    return formatMap[chartType]()
  }

  // 更新数据
  const updateOption = (options: Option, config) => {
    const { bar, line, pie, table } = config || {}
    const { chart: chartOptions, table: tableOptions, isCompare=false } = options
    setChartOptions({
      [BAR]: formatDataByChartType(BAR, chartOptions, bar),
      [LINE]: formatDataByChartType(LINE, chartOptions, line,isCompare),
      [TABLE]: formatDataByChartType(TABLE, tableOptions, table),
      [PIE]: formatDataByChartType(PIE, chartOptions, pie)
    })
  }

  // 更新组件配置
  const update = (options, config) => {
    updateOption(options, config)
  }

  // 下载文件
  const handleDownload = (tableOption: TableOption) => {
    const isOutsideDownload = onDownLoad?.(tableOption)
    const name = (tableOption.downloadTitle ?? title)?.replace(/\u0020/g, '_')
    const excelColumn = buildExcelColumns(tableOption?.option?.length ?? 0)

    // 如果需要外侧自定义 excel 数据则不走默认逻辑
    if (isOutsideDownload) return
    // 表头行
    const headerRow = {}
    // 表头顺序
    const header: string[] = []
    // 以 key 为顺序
    const keys: string[] = []

    tableOption?.option?.forEach(item => {
      header.push(item.header)
      keys.push(item.key)
    })
    header?.forEach((item, index) => {
      headerRow[excelColumn[index]] = item
    })
    const excelData = [headerRow]

    if (tableOption?.downloadData) {
      tableOption?.downloadData?.forEach((item) => {
        const itemData = {}
        keys?.forEach((key, index) => {
          itemData[excelColumn[index]] = item[key]
        })
        excelData.push(itemData)
      })
    } else {
      tableOption?.data?.forEach((item) => {
        const itemData = {}
        keys?.forEach((key, index) => {
          itemData[excelColumn[index]] = item[key]
        })
        excelData.push(itemData)
      })
    }

    // 导出excel
    exportExcel(name, excelData)
  };

  useEffect(() => {
    update(options, config)
  }, [JSON.stringify(options || {}), JSON.stringify(config)])

  useEffect(() => {
    onTabChange?.(activeTab)
  }, [activeTab])

  useEffect(() => {
    onChartChange?.(activeChart)
  }, [activeChart])

  useEffect(() => {
    if (!tabList) return
    setActiveTab(tabList?.[0]?.value)
  }, [JSON.stringify(tabList)])

  useEffect(() => {
    if (!activeChartProps) return
    setActiveChart(activeChartProps)
  }, [activeChartProps])

  return {
    activeTab,
    activeChart,
    chartOptions,
    setActiveTab,
    setActiveChart,
    handleDownload
  }
}

export default useOptions
